import React, { Component, Fragment } from "react";
import { Dispatch } from "redux";
import Helmet from "react-helmet";
import { connect } from "react-redux";
import moment from "moment";
import { Wrap } from "../../../components/Layout/Page";
import { getMonthYearObj } from "../../../utils/time";
import Loading from "../../../components/Loading";
import { getReportData } from "../../../redux/modules/monthlySummary/actionCreators";
import { selectActiveSite } from "../../../redux/modules/_BETA/selections";
import ActionSummary from "../../../components/ActionSummary";
import { composeReportData } from "./composeData";
import { SecondaryNav } from "../../../components/AppChrome/components/SecondaryNav";
import { SecondaryLinks } from "../../../utils/routes/reports";

interface ConnectedProps {
  appSetupComplete: Insite.AppState["appSetup"]["appSetupComplete"];
  isLoading: Insite.AppState["monthlySummary"]["loading"];
  isLoaded: Insite.AppState["monthlySummary"]["loaded"];
  shouldRequestMonthlySummary: Insite.AppState["requestSchedule"]["shouldRequestMonthlySummary"];
  error: Insite.AppState["monthlySummary"]["error"];
  data: Insite.AppState["monthlySummary"]["data"];
  sites: Insite.AppState["userSites"]["sites"];
  activeSite: Insite.AppState["betaSelections"]["activeSite"];
  selectedSites: Insite.AppState["betaSelections"]["selectedSites"];
}

interface DispatchProps {
  loadData: () => void;
  selectSite: (synco: Insite.Site["syncoId"]) => void;
}

interface MonthYear {
  timestamp: moment.Moment;
  month: string;
  year: string;
}

interface State {
  data: any; // TODO: shape
  selectedMonth: MonthYear;
  timestamp: moment.Moment | Date;
}
function mapStateToProps(state: Insite.AppState): ConnectedProps {
  return {
    appSetupComplete: state.appSetup.appSetupComplete,
    isLoading: state.monthlySummary.loading,
    isLoaded: state.monthlySummary.loaded,
    shouldRequestMonthlySummary:
      state.requestSchedule.shouldRequestMonthlySummary,
    error: state.monthlySummary.error,
    data: state.monthlySummary.data,
    sites: state.userSites.sites,
    activeSite: state.betaSelections.activeSite,
    selectedSites: state.betaSelections.selectedSites,
  };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    loadData: () => {
      dispatch(getReportData());
    },
    selectSite: (syncoId) => {
      dispatch(selectActiveSite(syncoId));
    },
  };
}

type Props = ConnectedProps & DispatchProps;

class Reports extends Component<Props, State> {
  static defaultProps = {
    shouldRequestMonthlySummary: true,
    isLoading: true,
    isLoaded: false,
  };
  constructor(props: Props) {
    super(props);
    const now = moment();

    this.state = {
      data: {},
      timestamp: now,
      selectedMonth: getMonthYearObj(now),
    };
  }

  componentDidMount() {
    if (this.props.shouldRequestMonthlySummary && this.props.appSetupComplete) {
      this.props.loadData();
    }

    if (this.props.isLoaded) {
      this.handleDataUpdate();
    }
  }

  componentDidUpdate({ data, shouldRequestMonthlySummary }: ConnectedProps) {
    // If no report data, request it
    if (
      this.props.shouldRequestMonthlySummary &&
      this.props.appSetupComplete &&
      !this.props.isLoading
    ) {
      this.props.loadData();
    }

    // If report data has changed, update it
    if (this.props.data !== data && this.props.isLoaded) {
      this.handleDataUpdate();
    }

    // If site has changed, request data
    if (
      !shouldRequestMonthlySummary &&
      this.props.shouldRequestMonthlySummary
    ) {
      this.props.loadData();
    }
  }

  handleDataUpdate() {
    this.setState({
      data: composeReportData(this.props.data, this.state.timestamp),
    });
  }

  handleSelectMonth = ({ value }: { value: moment.Moment }) => {
    this.setState({
      timestamp: moment(value).toDate(),
      selectedMonth: getMonthYearObj(value),
      data: composeReportData(this.props.data, value),
    });
  };

  handleSelectSite = ({ value: syncoId }: { value: string }) =>
    this.props.selectSite(syncoId);

  composeSiteOptions = () => {
    const { selectedSites, sites } = this.props;
    return selectedSites.map((id: string) => {
      const site = sites[id];
      return {
        label: site.label,
        value: site.syncoId,
      };
    });
  };

  render() {
    const { activeSite, isLoading, appSetupComplete } = this.props;
    const reportReady =
      appSetupComplete && this.state.data.atAGlance && !isLoading;

    return (
      <Fragment>
        <Helmet title="Monthly Summary" />
        <SecondaryNav links={SecondaryLinks} />

        <Wrap>
          {reportReady && appSetupComplete ? (
            <ActionSummary
              data={this.state.data.atAGlance}
              onMonthChange={this.handleSelectMonth}
              monthValue={this.state.selectedMonth}
              onSiteChange={this.handleSelectSite}
              siteValue={{ value: activeSite.syncoId, label: activeSite.label }}
              siteOptions={this.composeSiteOptions()}
            />
          ) : (
            <Loading />
          )}
        </Wrap>
      </Fragment>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Reports);
